import type { ComponentProps, ReactNode } from "react";

import { Tk, TranslationParams, useTranslation2 } from "../i18n";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./alert-dialog";

type Props = ComponentProps<typeof AlertDialog> & {
  titleT: TranslationParams.Type;
  descriptionProps?: Omit<ComponentProps<typeof AlertDialogDescription>, "children"> & {
    children?:
      | TranslationParams.Type
      | ((defaultTextT: TranslationParams.Type) => ReactNode)
      | undefined;
  };
  triggerProps?: ComponentProps<typeof AlertDialogTrigger> | undefined;
  onCancel?: (() => void) | undefined;
  onContinue?: (() => void) | undefined;
};

function DeleteDialog({
  titleT,
  descriptionProps: {
    asChild: descriptionAsChild = false,
    children: descriptionChildren,
    ...descriptionProps
  } = {},
  triggerProps: { asChild: triggerAsChild = true, ...triggerProps } = {},
  onCancel,
  onContinue,
  ...props
}: Props) {
  const {
    postproc: { tc, tsp },
  } = useTranslation2();

  const defaultDescriptionTextT = Tk.THIS_ACTION_CANNOT_BE_UNDONE;

  return (
    <AlertDialog {...props}>
      {triggerProps && <AlertDialogTrigger asChild={triggerAsChild} {...triggerProps} />}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{tc(...TranslationParams.toTuple(titleT))}</AlertDialogTitle>
          <AlertDialogDescription asChild={descriptionAsChild} {...descriptionProps}>
            {typeof descriptionChildren === "function"
              ? descriptionChildren(defaultDescriptionTextT)
              : tsp(...TranslationParams.toTuple(descriptionChildren ?? defaultDescriptionTextT))}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{tc(Tk.NO)}</AlertDialogCancel>
          <AlertDialogAction onClick={onContinue}>{tc(Tk.YES)}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { DeleteDialog };
